import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import { InviteProfessionalPopup } from '~/commonComponents/InviteProfessional/Popup';
import { IJob } from '~/models';

/** PostHog source label for invites sent from a shift-details page. */
const INVITE_SOURCE = 'Shift Details';

interface IUseShiftActionsMenuArgs {
  /**
   * The shift's id from the route, NOT `job.id`.
   *
   * The menu lives in the page header, which is up before the job query
   * resolves — so an office can open it and pick Edit while `job` is still
   * undefined. Routing off the job sent them to `/dashboard/job/undefined/edit`.
   */
  shiftId?: string;
  job: Partial<IJob> | null | undefined;
  /** Lifts the navigation guard before leaving the page for the edit flow. */
  allowNavigation: () => void;
}

/**
 * The page header's three-dot menu: Edit and Invite, both wired to the flows
 * that already exist.
 *
 * Neither action is new behaviour — Edit is the same `/edit` route the shift
 * cards use and Invite is the shared `InviteProfessionalPopup`. What this owns is
 * the popup's open state, which is why the popup element comes back with the
 * items rather than being left for the page to remember to render.
 */
export const useShiftActionsMenu = ({
  shiftId,
  job,
  allowNavigation,
}: IUseShiftActionsMenuArgs) => {
  const navigate = useNavigate();
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const items: CardMenuItem[] = [
    {
      label: 'Edit Shift',
      onSelect: () => {
        allowNavigation();
        navigate(`/dashboard/job/${shiftId}/edit`);
      },
      testID: 'shift-details-edit',
    },
    {
      label: 'Invite Professionals',
      onSelect: () => setIsInviteOpen(true),
      testID: 'shift-details-invite',
    },
  ];

  const invitePopup = isInviteOpen ? (
    <InviteProfessionalPopup
      isOpen
      isRefetchJob
      jobId={shiftId}
      profession={job?.profession}
      officeId={job?.offer_owner?.id}
      trackingSource={INVITE_SOURCE}
      onClose={() => setIsInviteOpen(false)}
    />
  ) : null;

  return { items, invitePopup };
};
